import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { setUserInfo } from "../../../../store/auth/authSlice";
import { useGetUserQuery } from "../../../../store/auth/usersApiSlice";

import { FollowedCompanyRow } from "./FollowedCompanyRow";
import { Pagination } from "../../../common/u1/Pagination";
import { SearchField } from "../../../common/u1/SearchField";
import { nameMatches } from "../../../common/u1/filters";

/* The businesses a user follows. */
export const PAGE_SIZE = 10;

/* Transcribed match rule: substring, case-insensitive, on the registered name. */
export const matchesQuery = (follow, query) => {
  const name = follow?.companyID?.registered_company_name;
  if (!name) return false;
  return nameMatches(name, query);
};

export const FollowingList = () => {
  const dispatch = useDispatch();
  const { userInfo, token } = useSelector((state) => state.auth);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  /* Re-read on arrival. `refetchOnMountOrArgChange` is what makes this a
     FETCH rather than a cache read: the args do not change between visits, so
     without it RTK Query would serve the answer it already had. */
  const { data: freshUser } = useGetUserQuery(token, {
    skip: !token,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (freshUser) dispatch(setUserInfo({ ...freshUser }));
  }, [freshUser, dispatch]);

  const follows = useMemo(() => userInfo?.companyFollow || [], [userInfo]);
  const filtered = useMemo(
    () => follows.filter((f) => matchesQuery(f, query)),
    [follows, query]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  /* Clamped during render: narrowing the search can leave the current page
     past the end, and correcting it in an effect would paint an empty list
     first. */
  const current = Math.min(page, totalPages);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const search = (value) => {
    setQuery(value);
    setPage(1);
  };

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="m-0 text-[19px] font-bold text-[var(--ink)] font-sans">
          Businesses you follow
        </h2>

        {/* Same filter the live screen runs, restyled. It narrows the list in
            place — it does not search the directory. */}
        <SearchField
          value={query}
          onChange={search}
          placeholder="Search company"
          label="Search the businesses you follow"
          className="sm:w-[320px]"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="mx-auto flex max-w-[420px] flex-col items-center gap-4 py-12 text-center">
          <p className="m-0 text-[14px] text-[var(--muted)] font-sans">
            {follows.length === 0
              ? "Follow businesses and stay updated about any changes."
              : "No businesses match that search."}
          </p>
          {follows.length === 0 && (
            /* Transcribed: the live empty state links to the directory in a
               new tab. Written on the Link rather than wrapping a Button —
               a block Link around a button gives two boxes to size. */
            <Link
              to="/search"
              target="_blank"
              className="inline-flex h-9 items-center justify-center rounded-lg border border-[var(--coral)] bg-white px-4 text-[13px] font-semibold text-[var(--coral)] font-sans transition-colors hover:bg-[var(--coral-soft)]"
            >
              Start Following
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-col gap-3">
            {visible.map((follow) => (
              <FollowedCompanyRow
                key={follow?.company_follow_ID || follow?.companyID?.companyID}
                follow={follow}
              />
            ))}
          </div>

          <Pagination
            page={current}
            totalPages={totalPages}
            total={filtered.length}
            pageSize={PAGE_SIZE}
            itemLabel="business"
            itemLabelPlural="businesses"
            onChange={setPage}
            label="Followed businesses pages"
          />
        </>
      )}
    </section>
  );
};

export default FollowingList;
